import { useState, useEffect, useCallback } from 'react'
import { BrowserProvider, Contract, Signer, InterfaceAbi } from 'ethers'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ToggleableAmount } from '@/components/ui/toggleable-amount'
import { Wallet, Fuel } from 'lucide-react'
import { formatEtherLimited, parseEther } from '@/lib/ethersUtils'
import { RaffleMiningABI } from '@/abi/RaffleMiningABI'
import { MultiSendABI } from '@/abi/MultiSendABI'
import { CONTRACT_ADDRESS, MULTISEND_ADDRESS } from '@/lib/contracts'
import { isPageVisible } from '@/lib/pageVisibility'
import { useTransactionToast } from '@/hooks/useTransactionToast'
import { validateBalance, isAmountExceedingBalance } from '@/lib/validateBalance'
import { cn } from '@/lib/utils'

// Configurable addresses from environment
const CRANK_OPERATOR_ADDRESS = import.meta.env.VITE_CRANK_OPERATOR_ADDRESS || ''
const GAS_VAULT_ADDRESS = import.meta.env.VITE_GAS_VAULT_ADDRESS || ''

const BALANCE_REFRESH_INTERVAL = 15000 // 15 seconds

// Target balances
const OPERATOR_TARGET = parseEther('2') // 2 BNB

// Vault fill threshold — below 90% of max, split 50/50
const VAULT_FILL_THRESHOLD_PERCENT = 90n

type HealthLevel = 'healthy' | 'low' | 'critical' | 'unknown'

function getHealthLevel(balance: bigint | null, targetBalance: bigint): HealthLevel {
  if (balance === null || targetBalance === 0n) return 'unknown'
  if (balance * 4n < targetBalance) return 'critical'  // < 25% of target
  if (balance * 2n < targetBalance) return 'low'        // < 50% of target
  return 'healthy'                                       // >= 50% of target
}

function HealthDot({ level }: { level: HealthLevel }) {
  const colors: Record<HealthLevel, string> = {
    healthy: 'bg-green-500',
    low: 'bg-yellow-500',
    critical: 'bg-red-500',
    unknown: 'bg-muted-foreground/50',
  }
  return <span className={`inline-block h-2 w-2 rounded-full ${colors[level]}`} />
}

interface GasStatusProps {
  provider: BrowserProvider | null
  signer: Signer | null
  walletBalance: bigint | undefined
  bnbToUsd: (wei: bigint) => string | null
}

export function GasStatus({ provider, signer, walletBalance, bnbToUsd }: GasStatusProps) {
  const [operatorBalance, setOperatorBalance] = useState<bigint | null>(null)
  const [vaultBalance, setVaultBalance] = useState<bigint | null>(null)
  const [maxGasChargePool, setMaxGasChargePool] = useState<bigint | null>(null)
  const [topUpAmount, setTopUpAmount] = useState('0.25')
  const [isTopUpPending, setIsTopUpPending] = useState(false)
  const { showTxPending, showTxError } = useTransactionToast()

  const hasAddresses = CRANK_OPERATOR_ADDRESS || GAS_VAULT_ADDRESS

  const fetchBalances = useCallback(async () => {
    if (!isPageVisible()) return
    if (!provider) return

    try {
      const [opBal, vaultBal] = await Promise.all([
        CRANK_OPERATOR_ADDRESS ? provider.getBalance(CRANK_OPERATOR_ADDRESS) : Promise.resolve(null),
        GAS_VAULT_ADDRESS ? provider.getBalance(GAS_VAULT_ADDRESS) : Promise.resolve(null),
      ])
      setOperatorBalance(opBal)
      setVaultBalance(vaultBal)

      // Fetch maxGasChargePool from RaffleMining contract
      if (CONTRACT_ADDRESS) {
        const rm = new Contract(CONTRACT_ADDRESS, RaffleMiningABI as InterfaceAbi, provider)
        const maxPool = await rm.maxGasChargePool()
        setMaxGasChargePool(maxPool)
      }
    } catch (error) {
      console.error('[GasStatus] Failed to fetch balances:', error)
    }
  }, [provider])

  useEffect(() => {
    fetchBalances()
    const interval = setInterval(fetchBalances, BALANCE_REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchBalances])

  if (!hasAddresses) return null

  const operatorHealth = getHealthLevel(operatorBalance, OPERATOR_TARGET)
  const vaultHealth = getHealthLevel(vaultBalance, maxGasChargePool ?? 0n)

  // Determine split: vault below 90% of max → 50/50, else 100% operator
  const vaultBelowThreshold =
    vaultBalance !== null &&
    maxGasChargePool !== null &&
    maxGasChargePool > 0n &&
    vaultBalance * 100n < maxGasChargePool * VAULT_FILL_THRESHOLD_PERCENT

  const splitLabel = vaultBelowThreshold ? '50 / 50' : '100% Operator'

  const handleTopUp = async () => {
    if (!signer || !topUpAmount || parseFloat(topUpAmount) <= 0) return

    const totalWei = parseEther(topUpAmount)
    if (totalWei === 0n) return

    // Pre-flight balance check
    const validation = validateBalance({ balance: walletBalance, value: totalWei })
    if (!validation.valid) {
      showTxError('Top Up Gas', validation.error!)
      return
    }

    setIsTopUpPending(true)
    try {
      let tx
      if (vaultBelowThreshold && MULTISEND_ADDRESS && CRANK_OPERATOR_ADDRESS && GAS_VAULT_ADDRESS) {
        // 50/50 split via MultiSend
        const half = totalWei / 2n
        const remainder = totalWei - half // accounts for odd wei

        const multiSend = new Contract(MULTISEND_ADDRESS, MultiSendABI as InterfaceAbi, signer)
        tx = await multiSend.multiSendETH(
          [GAS_VAULT_ADDRESS, CRANK_OPERATOR_ADDRESS],
          [half, remainder],
          { value: totalWei },
        )
      } else if (CRANK_OPERATOR_ADDRESS) {
        // 100% to operator — simple transfer
        tx = await signer.sendTransaction({
          to: CRANK_OPERATOR_ADDRESS,
          value: totalWei,
        })
      }

      if (tx) {
        const toastHandle = showTxPending('Top Up Gas', tx.hash)
        try {
          await tx.wait()
          toastHandle.success()
        } catch (error) {
          toastHandle.error(error instanceof Error ? error.message : 'Transaction failed')
        }
      }
      await fetchBalances()
    } catch (error) {
      console.error('[GasStatus] Top up failed:', error)
      showTxError('Top Up Gas', error instanceof Error ? error.message : 'Transaction failed')
    } finally {
      setIsTopUpPending(false)
    }
  }

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <Fuel className="h-4 w-4" />
          Gas
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-2 space-y-3">
        {/* Status Row — Operator and Vault on one line */}
        <div className="flex items-center gap-4 text-sm">
          {CRANK_OPERATOR_ADDRESS && (
            <div className="flex items-center gap-1.5 min-w-0">
              <HealthDot level={operatorHealth} />
              <span className="text-muted-foreground">Operator</span>
              {operatorBalance !== null ? (
                <ToggleableAmount
                  amount={formatEtherLimited(operatorBalance)}
                  symbol="BNB"
                  usdValue={bnbToUsd(operatorBalance)}
                  amountClassName="font-medium"
                />
              ) : (
                <span className="text-muted-foreground">--</span>
              )}
            </div>
          )}
          {GAS_VAULT_ADDRESS && (
            <div className="flex items-center gap-1.5 min-w-0">
              <HealthDot level={vaultHealth} />
              <span className="text-muted-foreground">Vault</span>
              {vaultBalance !== null ? (
                <ToggleableAmount
                  amount={formatEtherLimited(vaultBalance)}
                  symbol="BNB"
                  usdValue={bnbToUsd(vaultBalance)}
                  amountClassName="font-medium"
                />
              ) : (
                <span className="text-muted-foreground">--</span>
              )}
            </div>
          )}
        </div>

        {/* Top Up Section */}
        <div className="p-3 bg-muted/50 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium flex items-center gap-1">
              <Wallet className="h-3.5 w-3.5" />
              <ToggleableAmount
                amount={formatEtherLimited(walletBalance || 0n)}
                symbol="BNB"
                usdValue={bnbToUsd(walletBalance || 0n)}
              />
            </div>
            <span className="text-xs text-muted-foreground">{splitLabel}</span>
          </div>
          <div className="flex gap-2">
            <Input
              type="number"
              value={topUpAmount}
              onChange={(e) => setTopUpAmount(e.target.value)}
              className={cn("flex-1", isAmountExceedingBalance(topUpAmount, walletBalance || 0n) && "border-destructive text-destructive")}
              step="0.01"
              min="0.01"
              placeholder="BNB amount"
            />
            <Button
              onClick={handleTopUp}
              disabled={isTopUpPending || !signer || !topUpAmount || parseFloat(topUpAmount) <= 0}
            >
              {isTopUpPending ? '...' : 'Top Up'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
